import React, { useEffect, useState } from "react";
import { Col, Row } from "rsuite";
import {
	ComposedChart,
	Line,
	Area,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	Scatter,
} from "recharts";

interface DataPoint {
	name: string;
	uv: number;
	pv: [number, number];
	amt: number;
	cnt: number;
	FiscalWeek?: number;
	YearMonth?: string;
	CFPMin?: number;
	CFPHigh?: number;
	LeadingPrice?: number;
	FollowingPrice?: number;
}

interface HistoricalTrendGraphProps {
	data: DataPoint[];
	handleClick: (event: React.MouseEvent) => void;
}

const HistoricalTrendGraph: React.FC<HistoricalTrendGraphProps> = ({
	data,
	handleClick,
}) => {
	const [tableHeight, setTableHeight] = useState<number>(150);
	const [graphWidth, setGraphWidth] = useState<number>(600);
	const [isGraphHeightUpdate, setIsGraphHeightUpdate] =
		useState<boolean>(true);

	useEffect(() => {
		const updateGraphDimensions = () => {
			const id = document.getElementById("trendcontent");
			if (id) {
				setGraphWidth(id.offsetWidth - 50);
			}
			const id1 = document.getElementById("rowId");
			if (id1) {
				setTableHeight(id1.offsetHeight);
			}
		};

		updateGraphDimensions();

		window.addEventListener("resize", updateGraphDimensions);

		return () => {
			window.removeEventListener("resize", updateGraphDimensions);
		};
	}, []);

	useEffect(() => {
		if (data.length && isGraphHeightUpdate) {
			setIsGraphHeightUpdate(false);
			const id1 = document.getElementById("rowId");
			if (id1) {
				setTableHeight(id1.offsetHeight);
			}
		}
	}, [data, isGraphHeightUpdate]);

	const setDataStructureForGraph = (data: DataPoint[]) => {
		let barData: any = [];
		data.sort((a, b) => a.FiscalWeek! - b.FiscalWeek!).forEach((d) => {
			barData.push({
				Month: d.YearMonth,
				"Desired CFP Range": [
					Math.round(d.CFPMin! * 10) / 10,
					Math.round(d.CFPHigh! * 10) / 10,
				],
				NBE: d.LeadingPrice,
				"Brand Price": d.FollowingPrice,
			});
		});
		return barData;
	};

	const CustomTooltip = ({ active, payload, label }: any) => {
		if (active && payload && payload.length) {
			return (
				<div className="custom-tooltip  bg-[#1a1916]">
					<p className="label p-0 m-0 text-lightWhite text-[10px]">
						<label>
							Month{" "}
						</label>
						<label>{`${label}`}</label>
					</p>
					{payload.map((d: any) => (
						<p
							key={d.name}
							className="label p-0 m-0 text-lightWhite text-[10px]"
						>
							<label>{`${d.name} `}</label>
							<label>{`$${d.value}`}</label>
						</p>
					))}
				</div>
			);
		}
		return null;
	};

	const barData = setDataStructureForGraph(data);

	return (
		<div id="rowId" className="h-full">
			{barData.length ? (
				<ComposedChart
					className="ml-[8%]"
					width={graphWidth - 150}
					height={300}
					data={barData}
					margin={{
						top: 20,
						right: 20,
						bottom: 20,
						left: 30,
					}}
				>
					<CartesianGrid
						className="p-[1rem]"
						stroke="#989898"
						strokeDasharray="none"
						horizontal={false}
					/>
					<XAxis
						fontSize={"0.8vw"}
						tick={{
							stroke: "grey",
							fontSize: "0.8vw",
							strokeWidth: 0.3,
						}}
						label={{
							fontSize: "0.8vw",
							style: {
								marginTop: "1%",
								textAnchor: "middle",
								color: "white",
							},
							position: "bottom",
							fill: "white",
							font: {
								size: "0.8vw"
							}
						}}
						stroke="#989898"
						dataKey="Month"
					/>
					<YAxis
						tick={{
							stroke: "grey",
							fontSize: "0.8vw",
							strokeWidth: 0.3,
						}}
						stroke="#989898"
						tickFormatter={(value) => "$" + value}
						tickCount={5}
						type="number"
						domain={["dataMin - 3", "auto"]}
						interval="preserveStartEnd"
						label={{
							fontSize: "0.8vw",
							style: { textAnchor: "middle", color: "white" },
							value: "CFP ($/Case)",
							angle: -90,
							position: "insideLeft",
							fill: "black",
							font: {
								size: "0.8vw"
							}
						}}
					/>
					<Tooltip
						contentStyle={{
							color: "white",
							backgroundColor: "rgb(26, 25, 22)",
						}}
						content={<CustomTooltip />}
					/>
					<Legend
						align="right"
						verticalAlign="top"
						iconSize={14}
						height={30}
						wrapperStyle={{
							fontSize: "0.8vw",
						}}
					/>
					<Area
						type="monotone"
						dataKey="Desired CFP Range"
						fill="#648f66"
						stroke="#648f66"
					/>
					<Line
						type="monotone"
						dataKey="Brand Price"
						stroke="#db4c42"
					/>
					<Line type="monotone" dataKey="NBE" stroke="#e89c38" />
				</ComposedChart>
			) : (
				<span className="divTextCenter">Loading...</span>
			)}
		</div>
	);
};

export default HistoricalTrendGraph;
